import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useTheme } from '../useTheme';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  Cog6ToothIcon,
  XMarkIcon,
  MoonIcon,
  SunIcon,
  AcademicCapIcon,
  UserGroupIcon,
  UsersIcon,
  UserPlusIcon,
  Bars3Icon
} from '@heroicons/react/24/solid';

const Navbar = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { user, signOut } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const handleOpenSettings = () => {
    setEditName(user?.user_metadata?.name || '');
    setShowSettings(true);
    setShowDropdown(false);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    try {
      setIsSaving(true);
      // Update user profile in database
      await axios.put(`/api/users/${user.id}`, {
        name: editName.trim()
      });

      toast.success('Profile updated successfully!');
      setShowSettings(false);
      // Refresh the page to update user data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <div className="px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center">
            <img
              src="/Solomon.svg"
              alt="Solomon Logo"
              className="h-12 w-auto"
            />
            <span className="ml-3 text-xl font-bold text-gray-800 dark:text-white">Solomon</span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden lg:flex items-center space-x-3 xl:space-x-6">
            <Link
              to="/"
              className="flex items-center text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
            >
              <HomeIcon className="h-4 w-4 xl:h-5 xl:w-5 mr-1" />
              <span className="hidden xl:inline">Dashboard</span>
              <span className="xl:hidden">Home</span>
            </Link>

            {/* Admin Links - Only show for admin users */}
            {user?.user_metadata?.role === 'admin' && (
              <>
                <Link
                  to="/admin/requests"
                  className="flex items-center text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  <AcademicCapIcon className="h-4 w-4 xl:h-5 xl:w-5 mr-1" />
                  <span className="hidden xl:inline">Tutoring Requests</span>
                  <span className="xl:hidden">Requests</span>
                </Link>
                <Link
                  to="/admin/applications"
                  className="flex items-center text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  <UserGroupIcon className="h-4 w-4 xl:h-5 xl:w-5 mr-1" />
                  <span className="hidden xl:inline">Tutor Applications</span>
                  <span className="xl:hidden">Applications</span>
                </Link>
                <Link
                  to="/admin/tutors"
                  className="flex items-center text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  <UsersIcon className="h-4 w-4 xl:h-5 xl:w-5 mr-1" />
                  <span className="hidden xl:inline">Manage Tutors</span>
                  <span className="xl:hidden">Tutors</span>
                </Link>
                <Link
                  to="/admin/leads"
                  className="flex items-center text-sm xl:text-base text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  <UserPlusIcon className="h-4 w-4 xl:h-5 xl:w-5 mr-1" />
                  Leads
                </Link>
              </>
            )}
          </div>

          {/* User Menu & Theme Toggle */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Mobile Menu Button - Admin Only */}
            {user?.user_metadata?.role === 'admin' && (
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Menu"
              >
                {showMobileMenu ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            )}

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white focus:outline-none"
              >
                <UserCircleIcon className="h-8 w-8" />
                <span className="ml-2 text-sm font-medium hidden sm:block">{user?.user_metadata?.name || user?.email}</span>
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.user_metadata?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                    <span className="inline-block mt-1 px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full capitalize">
                      {user?.user_metadata?.role || 'Student'}
                    </span>
                  </div>

                  <button
                    onClick={handleOpenSettings}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Cog6ToothIcon className="h-4 w-4 mr-2" />
                    Settings
                  </button>

                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu - Admin Only */}
        {showMobileMenu && user?.user_metadata?.role === 'admin' && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <HomeIcon className="h-5 w-5 mr-3" />
              Dashboard
            </Link>
            <Link
              to="/admin/requests"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <AcademicCapIcon className="h-5 w-5 mr-3" />
              Tutoring Requests
            </Link>
            <Link
              to="/admin/applications"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <UserGroupIcon className="h-5 w-5 mr-3" />
              Tutor Applications
            </Link>
            <Link
              to="/admin/tutors"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <UsersIcon className="h-5 w-5 mr-3" />
              Manage Tutors
            </Link>
            <Link
              to="/admin/leads"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <UserPlusIcon className="h-5 w-5 mr-3" />
              Leads
            </Link>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Account Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveProfile} className="px-6 py-4">
              {/* Account Information */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Account Information</h3>

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Your name"
                  />
                </div>

                {/* Email (read-only) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Email cannot be changed</p>
                </div>

                {/* Role (read-only) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <div className="flex items-center">
                    <span className="inline-block px-3 py-1 text-sm bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full font-medium capitalize">
                      {user?.user_metadata?.role || 'Student'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Stats */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Account Details</h3>
                <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <p><span className="font-medium">User ID:</span> {user?.id?.slice(0, 8)}...</p>
                  <p><span className="font-medium">Account created:</span> {new Date(user?.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
